import { usePageMeta } from "../lib/seo";
import { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { motion } from "framer-motion";
import { MapPin, Calendar, Car, Trash2, Plus, Route, Loader2, Crown, MessageCircleHeart, BedDouble, RotateCcw, ChevronDown, Euro, Zap, Gauge, Leaf, Bike, Warehouse } from "lucide-react";
import { co2SavedKg, co2Trees } from "../lib/co2";
import { toast } from "sonner";
import { api, formatApiError, API_BASE } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { Navbar } from "../components/Navbar";
import { MapPhotoImport } from "../components/MapPhotoImport";
import { ReferralCard } from "../components/ReferralCard";
import { InstallAppCard } from "../components/InstallAppCard";
import { Badge } from "../components/ui/badge";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "../components/ui/dialog";
import { Input } from "../components/ui/input";

const statusLabels = {
  generating: { label: "Genereren...", cls: "bg-amber-100 text-amber-800" },
  ready: { label: "Klaar", cls: "bg-emerald-100 text-emerald-800" },
  active: { label: "Live onderweg", cls: "bg-blue-100 text-blue-800" },
  completed: { label: "Voltooid", cls: "bg-gray-200 text-gray-700" },
  error: { label: "Fout", cls: "bg-red-100 text-red-700" },
};

export default function Dashboard() {
  usePageMeta("Mijn trips — VoltRoute");
  const navigate = useNavigate();
  const { user } = useAuth();
  const [trips, setTrips] = useState(null);
  const [searchParams, setSearchParams] = useSearchParams();
  const upgradeRef = useRef(false);
  const [discountCode, setDiscountCode] = useState("");
  const [trash, setTrash] = useState([]);
  const [trashOpen, setTrashOpen] = useState(false);
  const [confirmTrip, setConfirmTrip] = useState(null);
  const [confirmText, setConfirmText] = useState("");
  const [deleting, setDeleting] = useState(false);

  const load = useCallback(() => api.get("/trips").then((r) => setTrips(r.data)).catch(() => setTrips([])), []);
  const loadTrash = useCallback(() => api.get("/trips/trash").then((r) => setTrash(r.data)).catch(() => {}), []);
  useEffect(() => { load(); loadTrash(); }, [load, loadTrash]);

  const startCheckout = async (plan, code) => {
    try {
      const body = { plan };
      const c = (code ?? discountCode).trim();
      if (c) body.discount_code = c;
      const { data } = await api.post("/billing/checkout", body);
      window.location.href = data.checkout_url;
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    }
  };

  useEffect(() => {
    const plan = searchParams.get("plan");
    const code = searchParams.get("code") || "";
    if (plan && !upgradeRef.current && (plan === "pro" || plan === "family")) {
      upgradeRef.current = true;
      if (code) setDiscountCode(code);
      setSearchParams({}, { replace: true });
      startCheckout(plan, code);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams]);

  const askDelete = (e, trip) => {
    e.stopPropagation();
    setConfirmTrip(trip);
    setConfirmText("");
  };

  const confirmDelete = async () => {
    setDeleting(true);
    try {
      await api.delete(`/trips/${confirmTrip.id}`);
      toast.success("Trip naar de prullenbak verplaatst — 30 dagen te herstellen");
      setConfirmTrip(null);
      load();
      loadTrash();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setDeleting(false);
    }
  };

  const restoreTrip = async (id) => {
    try {
      await api.post(`/trips/${id}/restore`);
      toast.success("Trip hersteld!");
      load();
      loadTrash();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    }
  };

  return (
    <div className="min-h-screen bg-background">
      <Navbar />
      <main className="max-w-7xl mx-auto px-4 sm:px-6 py-10">
        <p className="text-xs tracking-[0.15em] uppercase font-semibold text-accent mb-2">Jouw roadtrips</p>
        <div className="flex flex-wrap items-center justify-between gap-3 mb-6">
          <h1 className="font-heading font-extrabold text-3xl sm:text-4xl tracking-tight">Waar gaan we heen?</h1>
          <div className="flex flex-wrap gap-2">
            <button data-testid="my-garage-link" onClick={() => navigate("/garage")}
              className="rounded-full border border-border bg-card hover:bg-secondary px-4 py-2 text-xs font-bold flex items-center gap-1.5 active:scale-95 transition-[transform,background-color] duration-200">
              <Warehouse className="w-4 h-4 text-primary" /> Mijn garage
            </button>
            <button data-testid="my-stays-link" onClick={() => navigate("/overnachtingen")}
              className="rounded-full border border-border bg-card hover:bg-secondary px-4 py-2 text-xs font-bold flex items-center gap-1.5 active:scale-95 transition-[transform,background-color] duration-200">
              <BedDouble className="w-4 h-4 text-primary" /> Mijn overnachtingen
            </button>
          </div>
        </div>

        {user?.plan === "free" && (
          <div data-testid="upgrade-banner" className="bg-primary/5 border border-primary/15 rounded-2xl p-4 sm:p-5 mb-8 flex flex-col sm:flex-row sm:items-center gap-3">
            <div className="flex items-center gap-3 flex-1">
              <span className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center shrink-0"><Crown className="w-5 h-5 text-primary" /></span>
              <p className="text-sm text-muted-foreground">Je gebruikt het <strong className="text-foreground">Free</strong>-plan. Upgrade voor onbeperkt trips, voice-journal en live modus.</p>
            </div>
            <div className="flex flex-wrap gap-2 shrink-0 items-center">
              <input data-testid="discount-code-input" value={discountCode} onChange={(e) => setDiscountCode(e.target.value)}
                placeholder="Kortingscode" maxLength={20}
                className="h-9 w-32 rounded-full bg-background border border-border px-3.5 text-xs font-mono uppercase outline-none focus:ring-2 focus:ring-primary/40 transition-[box-shadow] duration-200" />
              <button data-testid="upgrade-pro-button" onClick={() => startCheckout("pro")}
                className="rounded-full bg-primary hover:bg-[#1B4332] text-primary-foreground px-5 py-2.5 text-xs font-bold active:scale-95 transition-[transform,background-color] duration-200">
                Pro — €7,99/mnd
              </button>
              <button data-testid="upgrade-family-button" onClick={() => startCheckout("family")}
                className="rounded-full bg-secondary hover:bg-secondary/70 px-5 py-2.5 text-xs font-bold active:scale-95 transition-[transform,background-color] duration-200">
                Family — €12,99/mnd
              </button>
            </div>
          </div>
        )}

        <InstallAppCard />

        <ReferralCard />

        {trips !== null && (() => {
          const totalKm = trips.filter((t) => t.travel_mode !== "moto").reduce((s, t) => s + (t.distance_km || 0), 0);
          if (totalKm <= 0) return null;
          const kg = co2SavedKg(totalKm);
          const trees = co2Trees(totalKm);
          return (
            <div data-testid="co2-dashboard-card" className="bg-[#2D6A4F]/[0.07] border border-[#2D6A4F]/20 rounded-2xl p-4 sm:p-5 mb-8 flex items-center gap-4">
              <span className="w-11 h-11 rounded-full bg-[#2D6A4F] flex items-center justify-center shrink-0">
                <Leaf className="w-5 h-5 text-white" />
              </span>
              <div>
                <p className="font-heading font-bold text-foreground">
                  <span className="font-mono text-[#2D6A4F]">{kg.toLocaleString("nl-BE")} kg CO₂</span> bespaard met je elektrische roadtrips
                </p>
                <p className="text-xs text-muted-foreground mt-0.5">
                  {totalKm.toLocaleString("nl-BE")} km volledig elektrisch t.o.v. een benzineauto (±170 g/km) — evenveel als <strong className="text-[#2D6A4F]">{trees.toLocaleString("nl-BE")} {trees === 1 ? "boom" : "bomen"}</strong> die een jaar lang CO₂ opnemen 🌳
                </p>
              </div>
            </div>
          );
        })()}

        {trips === null ? (
          <div className="flex justify-center py-20"><Loader2 className="w-8 h-8 animate-spin text-primary" /></div>
        ) : (
          <div data-testid="trips-grid" className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            <div
              data-testid="new-trip-card"
              onClick={() => navigate("/nieuwe-trip")}
              role="button"
              tabIndex={0}
              className="rounded-2xl border-2 border-dashed border-primary/40 hover:border-primary bg-primary/5 hover:bg-primary/10 min-h-[210px] flex flex-col items-center justify-center gap-3 transition-[background-color,border-color] duration-200 active:scale-[0.98] cursor-pointer"
            >
              <span className="w-12 h-12 rounded-full bg-primary flex items-center justify-center">
                <Plus className="w-6 h-6 text-primary-foreground" />
              </span>
              <span className="font-heading font-bold text-primary">Nieuwe roadtrip plannen</span>
              <div onClick={(e) => e.stopPropagation()}>
                <MapPhotoImport className="text-xs !px-3 !py-1.5"
                  onResult={(p) => { sessionStorage.setItem("vr_wizard_prefill", JSON.stringify(p)); navigate("/nieuwe-trip"); }} />
              </div>
            </div>

            {trips.map((trip, i) => {
              const today = new Date().toISOString().slice(0, 10);
              const isPast = trip.end_date && trip.end_date < today;
              const st = isPast && (trip.status === "active" || trip.status === "ready") ? statusLabels.completed : (statusLabels[trip.status] || statusLabels.ready);
              const seen = localStorage.getItem(`vr_comments_seen_${trip.id}`) || "";
              const unread = (trip.guest_comment_dates || []).filter((d) => d > seen).length;
              return (
                <motion.div
                  key={trip.id}
                  initial={{ opacity: 0, y: 15 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: i * 0.06 }}
                  data-testid={`trip-card-${trip.id}`}
                  onClick={() => navigate(`/trip/${trip.id}`)}
                  className="bg-card rounded-2xl p-6 shadow-[0_8px_32px_rgba(31,38,59,0.08)] cursor-pointer hover:-translate-y-1 transition-[transform,box-shadow] duration-300 relative group"
                >
                  <div className="flex items-start justify-between mb-4">
                    <div className="flex flex-wrap gap-1.5">
                      <Badge className={`${st.cls} rounded-full font-medium`} variant="secondary">{st.label}</Badge>
                      {trip.travel_mode === "moto" ? (
                        <Badge data-testid={`trip-mode-badge-${trip.id}`} className="bg-[#E76F51]/12 text-[#C1502E] rounded-full font-semibold" variant="secondary">
                          <Bike className="w-3 h-3 mr-1" /> Moto
                        </Badge>
                      ) : (
                        <Badge data-testid={`trip-mode-badge-${trip.id}`} className="bg-[#2D6A4F]/10 text-[#2D6A4F] rounded-full font-semibold" variant="secondary">
                          <Zap className="w-3 h-3 mr-1" /> Elektrisch
                        </Badge>
                      )}
                      {trip.is_owner === false && <Badge className="bg-[#3A6EA5]/10 text-[#3A6EA5] rounded-full font-medium" variant="secondary">Gedeeld · {trip.my_role}</Badge>}
                      {unread > 0 && (
                        <Badge data-testid={`trip-card-comments-badge-${trip.id}`}
                          onClick={(e) => { e.stopPropagation(); navigate(`/trip/${trip.id}?reacties=1`); }}
                          title="Bekijk alle reacties in één overzicht"
                          className="bg-[#B5838D]/15 text-[#8E5D67] rounded-full font-medium cursor-pointer hover:bg-[#B5838D]/30 transition-[background-color] duration-200" variant="secondary">
                          <MessageCircleHeart className="w-3 h-3 mr-1" /> {unread} {unread === 1 ? "nieuwe reactie" : "nieuwe reacties"}
                        </Badge>
                      )}
                    </div>
                    {trip.is_owner !== false && (
                      <button
                        data-testid={`delete-trip-${trip.id}`}
                        onClick={(e) => askDelete(e, trip)}
                        className="opacity-0 group-hover:opacity-100 p-2 rounded-full hover:bg-red-50 text-red-500 transition-[opacity,background-color] duration-200"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    )}
                  </div>
                  {trip.vehicle_photo_id && (
                    <img data-testid={`trip-card-photo-${trip.id}`} src={`${API_BASE}/trips/${trip.id}/photos/${trip.vehicle_photo_id}`} alt="Onze auto"
                      className="w-full h-28 object-cover rounded-xl mb-4" loading="lazy" />
                  )}
                  <h3 className="font-heading font-bold text-xl mb-3">{trip.name}</h3>
                  <div className="space-y-2 text-sm text-muted-foreground">
                    <p className="flex items-center gap-2"><MapPin className="w-4 h-4 text-primary shrink-0" /> {trip.origin_query} → {trip.destination_query}</p>
                    <p className="flex items-center gap-2"><Calendar className="w-4 h-4 text-primary shrink-0" /> {trip.start_date} t/m {trip.end_date}</p>
                    {trip.vehicle && <p className="flex items-center gap-2">{trip.travel_mode === "moto" ? <Bike className="w-4 h-4 text-[#C1502E] shrink-0" /> : <Car className="w-4 h-4 text-primary shrink-0" />} {trip.vehicle.brand} {trip.vehicle.model}</p>}
                    {trip.distance_km && <p className="flex items-center gap-2"><Route className="w-4 h-4 text-primary shrink-0" /> <span className="font-mono">{trip.distance_km} km</span> · {trip.num_days} {trip.num_days === 1 ? "dag" : "dagen"}</p>}
                  </div>
                  {trip.stats && (
                    <div data-testid={`trip-stats-${trip.id}`} className="mt-4 pt-3 border-t border-border/70">
                      <p className="text-[10px] font-bold uppercase tracking-wider text-muted-foreground mb-1.5">
                        Tripresultaat {trip.stats.has_actuals ? "(werkelijk)" : "(raming)"}
                      </p>
                      <div className="flex flex-wrap gap-x-4 gap-y-1 text-sm">
                        <span className="flex items-center gap-1.5 font-mono font-bold text-foreground"><Euro className="w-3.5 h-3.5 text-[#2D6A4F]" />{Math.round(trip.stats.cost_eur)}</span>
                        {trip.travel_mode !== "moto" && <span className="flex items-center gap-1.5 font-mono text-muted-foreground"><Zap className="w-3.5 h-3.5 text-[#3A6EA5]" />{Math.round(trip.stats.kwh)} kWh</span>}
                        {trip.travel_mode !== "moto" && trip.stats.avg_kwh_100km != null && <span className="flex items-center gap-1.5 font-mono text-muted-foreground"><Gauge className="w-3.5 h-3.5 text-[#B5838D]" />{trip.stats.avg_kwh_100km} kWh/100km</span>}
                      </div>
                    </div>
                  )}
                </motion.div>
              );
            })}
          </div>
        )}

        {trash.length > 0 && (
          <div className="mt-10" data-testid="trash-section">
            <button data-testid="trash-section-toggle" onClick={() => setTrashOpen((o) => !o)}
              className="flex items-center gap-2 text-sm font-semibold text-muted-foreground hover:text-foreground transition-[color] duration-200">
              <Trash2 className="w-4 h-4" /> Prullenbak ({trash.length})
              <ChevronDown className={`w-4 h-4 transition-transform duration-200 ${trashOpen ? "rotate-180" : ""}`} />
            </button>
            {trashOpen && (
              <div className="mt-3 space-y-2">
                {trash.map((t) => (
                  <div key={t.id} data-testid={`trash-item-${t.id}`} className="bg-card rounded-2xl px-5 py-3.5 flex flex-wrap items-center justify-between gap-3">
                    <div className="min-w-0">
                      <p className="font-heading font-bold text-sm truncate">{t.name}</p>
                      <p className="text-xs text-muted-foreground">
                        {t.origin_query} → {t.destination_query} · {t.start_date} t/m {t.end_date} · <span className="text-red-500 font-semibold">nog {t.days_left} {t.days_left === 1 ? "dag" : "dagen"} te herstellen</span>
                      </p>
                    </div>
                    <button data-testid={`restore-trip-${t.id}`} onClick={() => restoreTrip(t.id)}
                      className="rounded-full bg-primary hover:bg-[#1B4332] text-primary-foreground px-4 py-2 text-xs font-bold flex items-center gap-1.5 active:scale-95 transition-[transform,background-color] duration-200">
                      <RotateCcw className="w-3.5 h-3.5" /> Herstel
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        <Dialog open={!!confirmTrip} onOpenChange={(o) => !o && setConfirmTrip(null)}>
          <DialogContent className="rounded-2xl max-w-md" data-testid="delete-confirm-dialog">
            <DialogHeader>
              <DialogTitle className="font-heading">Trip verwijderen?</DialogTitle>
              <DialogDescription>
                "{confirmTrip?.name}" gaat 30 dagen in de prullenbak en kan in die periode hersteld worden. Typ de naam van de trip om te bevestigen.
              </DialogDescription>
            </DialogHeader>
            <Input data-testid="delete-confirm-input" value={confirmText} onChange={(e) => setConfirmText(e.target.value)}
              placeholder={confirmTrip?.name} className="rounded-xl" autoFocus />
            <div className="flex gap-2 justify-end">
              <button data-testid="delete-confirm-cancel" onClick={() => setConfirmTrip(null)}
                className="rounded-full bg-secondary hover:bg-secondary/70 px-5 py-2.5 text-xs font-bold transition-[background-color] duration-200">
                Annuleer
              </button>
              <button data-testid="delete-confirm-button" onClick={confirmDelete}
                disabled={deleting || confirmText.trim().toLowerCase() !== (confirmTrip?.name || "").trim().toLowerCase()}
                className="rounded-full bg-red-600 hover:bg-red-700 text-white px-5 py-2.5 text-xs font-bold disabled:opacity-40 transition-[background-color] duration-200">
                {deleting ? "Verwijderen..." : "Verplaats naar prullenbak"}
              </button>
            </div>
          </DialogContent>
        </Dialog>
      </main>
    </div>
  );
}
